import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Upload, Copy, CheckCircle2, ShieldCheck, ArrowRight, Cpu } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { TIER_META, api } from "../lib/api";
import { toast } from "sonner";

export default function BotSubscriptionModal({ open, tier, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [proofFile, setProofFile] = useState(null);
  const [crypto, setCrypto] = useState("BTC");
  const [wallets, setWallets] = useState({ btc_address: "", eth_address: "", usdt_address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (open && tier) {
      setStep(1);
      setProofFile(null);
      api.get("/settings").then((r) => setWallets(r.data));
    }
  }, [open, tier]);

  if (!tier) return null;

  const meta = TIER_META[tier];
  const addr = crypto === "BTC" ? wallets.btc_address : crypto === "ETH" ? wallets.eth_address : wallets.usdt_address;

  const copyAddr = () => {
    navigator.clipboard.writeText(addr);
    setCopied(crypto);
    setTimeout(() => setCopied(""), 1500);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/bot/subscribe", { tier, crypto, tx_proof: proofFile?.name || null });
      toast.success("AI Bot subscription submitted!", { description: "Your payment is under review. We'll activate your bot once confirmed." });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="bot-subscription-modal"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-strong rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <div className="label-eyebrow">AI Bot Subscription - Step {step} of 2</div>
                <div className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-[#D4AF37]" /> {meta.name} Bot Activation
                </div>
              </div>
              <button onClick={onClose} data-testid="bot-modal-close"><X className="w-5 h-5 text-white/60 hover:text-white" /></button>
            </div>

            <div className="px-6 py-6">
              {step === 1 && (
                <div className="space-y-5" data-testid="bot-step-payment">
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <ShieldCheck className="w-5 h-5" /> <span className="text-sm font-semibold">Activate Your AI Trading Bot</span>
                  </div>
                  <p className="text-sm text-white/70">
                    Subscribe to the <span className="text-[#D4AF37] font-bold">{meta.name}</span> tier to set your bot's ROI at <span className="text-[#00D68F] font-bold">{meta.roi}%</span> and unlock real investment access. Send <span className="text-[#D4AF37] font-bold">${meta.amount.toLocaleString()}</span> in crypto below.
                  </p>

                  <div className="flex gap-2">
                    {["BTC","ETH","USDT"].map(c => (
                      <button
                        key={c}
                        onClick={() => setCrypto(c)}
                        className={`flex-1 py-2 rounded-md border text-sm transition-colors ${crypto === c ? "bg-[#D4AF37] text-[#0A0E27] border-[#D4AF37]" : "border-white/10 text-white/70 hover:border-white/30"}`}
                        data-testid={`bot-crypto-${c}`}
                      >
                        {c === "USDT" ? "USDT (TRC-20)" : c}
                      </button>
                    ))}
                  </div>

                  <div className="glass rounded-xl p-5 flex flex-col sm:flex-row gap-5 items-center">
                    <div className="bg-white p-3 rounded-lg">
                      <QRCodeSVG value={addr || "duocapital"} size={128} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="label-eyebrow mb-2">Wallet Address</div>
                      <div className="font-mono text-xs break-all bg-black/30 p-3 rounded">{addr}</div>
                      <button onClick={copyAddr} className="mt-3 flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#FCEB9E]" data-testid="bot-copy-address">
                        {copied === crypto ? <><CheckCircle2 className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy address</>}
                      </button>
                    </div>
                  </div>

                  <div className="glass rounded-lg p-3 flex items-center gap-2 text-xs text-[#00D68F]">
                    <ShieldCheck className="w-4 h-4" /> Verified & Secured - Wallet rotates per session for security
                  </div>

                  <button onClick={() => setStep(2)} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="bot-step1-next">
                    I've sent the payment <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5" data-testid="bot-step-proof">
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <Upload className="w-5 h-5" /> <span className="text-sm font-semibold">Submit Payment Proof</span>
                  </div>
                  <p className="text-sm text-white/70">Upload a screenshot of your transaction. Your AI Bot will be activated by an admin once confirmed.</p>
                  <label className="block">
                    <div className="border-2 border-dashed border-white/15 rounded-xl p-8 text-center cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                      <Upload className="w-8 h-8 mx-auto text-[#D4AF37] mb-3" />
                      <div className="text-sm">{proofFile ? proofFile.name : "Upload transaction screenshot"}</div>
                    </div>
                    <input type="file" className="hidden" onChange={(e) => setProofFile(e.target.files[0])} data-testid="bot-proof-upload" accept="image/*" />
                  </label>

                  <button onClick={submit} disabled={submitting || !proofFile} className="btn-primary w-full disabled:opacity-40" data-testid="bot-step2-submit">
                    {submitting ? "Submitting..." : "Submit AI Bot Subscription"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}